import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type {
  AdminAccountCreateRequest,
  AdminAccountDetail,
  AdminAccountUpdateRequest,
  AdminRolesReplaceRequest,
} from '../types/admin';
import { adminAccountQueryKey } from './useAdminAccountsQuery';

const BASE = '/admin/v1/admins';

interface DetailEnvelope {
  success: boolean;
  data?: AdminAccountDetail | null;
}

const parseDetail = async (res: Response, op: string): Promise<AdminAccountDetail> => {
  if (!res.ok) throw new Error(`admin ${op} failed: ${res.status}`);
  const env = (await res.json()) as DetailEnvelope;
  if (!env.data) throw new Error('no data');
  return env.data;
};

/** Adds the explicit Idempotency-Key write header on top of the standard auth headers. */
const writeHeaders = (method: string, path: string, body: unknown): HeadersInit => ({
  'Idempotency-Key': idempotencyKeyFor(method, path, body),
});

export const useAdminAccountMutations = () => {
  const qc = useQueryClient();
  const invalidateList = () => qc.invalidateQueries({ queryKey: ['admin', 'admins'] });

  const create = useMutation({
    mutationFn: async (body: AdminAccountCreateRequest): Promise<AdminAccountDetail> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: writeHeaders('POST', BASE, body),
        body: JSON.stringify(body),
      });
      return parseDetail(res, 'create');
    },
    onSuccess: invalidateList,
  });

  const update = useMutation({
    mutationFn: async ({
      adminUserId,
      body,
    }: {
      adminUserId: string;
      body: AdminAccountUpdateRequest;
    }): Promise<AdminAccountDetail> => {
      const path = `${BASE}/${adminUserId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: writeHeaders('PUT', path, body),
        body: JSON.stringify(body),
      });
      return parseDetail(res, 'update');
    },
    onSuccess: (_data, { adminUserId }) => {
      void qc.invalidateQueries({ queryKey: adminAccountQueryKey(adminUserId) });
      invalidateList();
    },
  });

  const replaceRoles = useMutation({
    mutationFn: async ({
      adminUserId,
      body,
    }: {
      adminUserId: string;
      body: AdminRolesReplaceRequest;
    }): Promise<AdminAccountDetail> => {
      const path = `${BASE}/${adminUserId}/roles`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: writeHeaders('PUT', path, body),
        body: JSON.stringify(body),
      });
      return parseDetail(res, 'replaceRoles');
    },
    onSuccess: (_data, { adminUserId }) => {
      void qc.invalidateQueries({ queryKey: adminAccountQueryKey(adminUserId) });
      invalidateList();
    },
  });

  const deactivate = useMutation({
    mutationFn: async (adminUserId: string): Promise<void> => {
      const path = `${BASE}/${adminUserId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: writeHeaders('DELETE', path, ''),
      });
      if (!res.ok) throw new Error(`admin deactivate failed: ${res.status}`);
    },
    onSuccess: (_data, adminUserId) => {
      void qc.invalidateQueries({ queryKey: adminAccountQueryKey(adminUserId) });
      invalidateList();
    },
  });

  return { create, update, replaceRoles, deactivate };
};
